/**
 * Renderer-free procedural quadruped pose generation.
 *
 * Feet are stored in world space during stance. That is the important detail:
 * the body moves over a planted paw instead of dragging the paw along with it.
 * The animation driver converts the resulting pose to Three.js objects, keeping
 * gait math and spring behavior unit-testable without a renderer.
 */

import type { Vec3 } from '@/core/math';
import { angleDelta, clamp, damp, dot, lengthOf, normalize, remap, sub, vec3 } from '@/core/math';
import {
  tuning,
  type CatLocomotionTuning,
  type QuadrupedGaitName,
  type QuadrupedLegName,
  type QuadrupedPhaseOffsets,
} from '@/core/tuning';
import type { PhysicsWorld } from '@/engine/physicsWorld';

export type Gait = QuadrupedGaitName;
export type LegName = QuadrupedLegName;

export const LEG_ORDER: readonly LegName[] = ['frontLeft', 'frontRight', 'hindLeft', 'hindRight'];

export interface SkeletonDescription {
  legRootOffsets: Record<LegName, Vec3>;
  legLength: number;
  bodyLength: number;
  tailJointCount: number;
}

export interface SpringState {
  position: number;
  velocity: number;
}

export interface LegPose {
  phase: number;
  stance: boolean;
  root: Vec3;
  knee: Vec3;
  foot: Vec3;
  /** Exposed for planting tests and debug tooling. */
  footWorld: Vec3;
}

export interface QuadrupedPose {
  gait: Gait;
  phase: number;
  legs: Record<LegName, LegPose>;
  spinePitch: number;
  spineRoll: number;
  headYaw: number;
  headPitch: number;
  tail: number[];
  ears: [number, number];
}

export interface LocomotionFrame {
  dt: number;
  position: Vec3;
  velocity: Vec3;
  yaw: number;
  grounded: boolean;
  headLookTarget?: Vec3;
}

/** The subset of PhysicsWorld needed by the presentation pose. */
export type GroundProbe = Pick<PhysicsWorld, 'groundHeightAt'>;

interface LegState {
  initialized: boolean;
  inStance: boolean;
  footWorld: Vec3;
  swingStart: Vec3;
}

const IDLE_PHASE_OFFSETS: QuadrupedPhaseOffsets = [0, 0, 0, 0];

export function createSkeletonDescription(
  config: CatLocomotionTuning = tuning.cat.locomotion,
): SkeletonDescription {
  const offsets = config.skeleton.legRootOffsets;
  return {
    legRootOffsets: {
      frontLeft: tupleToVec3(offsets.frontLeft),
      frontRight: tupleToVec3(offsets.frontRight),
      hindLeft: tupleToVec3(offsets.hindLeft),
      hindRight: tupleToVec3(offsets.hindRight),
    },
    legLength: config.skeleton.legLength,
    bodyLength: config.skeleton.bodyLength,
    tailJointCount: config.tailJointCount,
  };
}

export function gaitForSpeed(
  speed: number,
  thresholds: CatLocomotionTuning['gaitSpeedThresholds'] = tuning.cat.locomotion
    .gaitSpeedThresholds,
): Gait {
  if (speed < thresholds.walk) return 'idle';
  if (speed < thresholds.trot) return 'walk';
  if (speed < thresholds.run) return 'trot';
  return 'run';
}

export function strideLengthForGait(
  gait: Gait,
  config: CatLocomotionTuning = tuning.cat.locomotion,
): number {
  if (gait === 'idle') return config.strideLength * config.strideScale.walk;
  return config.strideLength * config.strideScale[gait];
}

export function strideLengthForSpeed(
  speed: number,
  gait: Gait,
  config: CatLocomotionTuning = tuning.cat.locomotion,
): number {
  if (gait === 'walk') {
    return (
      config.strideLength *
      remap(
        speed,
        config.gaitSpeedThresholds.walk,
        config.gaitSpeedThresholds.trot,
        config.strideScale.walk,
        config.strideScale.trot,
      )
    );
  }
  if (gait === 'trot') {
    return (
      config.strideLength *
      remap(
        speed,
        config.gaitSpeedThresholds.trot,
        config.gaitSpeedThresholds.run,
        config.strideScale.trot,
        config.strideScale.run,
      )
    );
  }
  return strideLengthForGait(gait, config);
}

/** Cycles per second. One cycle advances the body by one configured stride. */
export function gaitFrequency(
  speed: number,
  gait: Gait,
  config: CatLocomotionTuning = tuning.cat.locomotion,
): number {
  if (gait === 'idle') return 0;
  return (speed / strideLengthForSpeed(speed, gait, config)) * config.stepFrequencyScale;
}

export function legPhaseAt(
  gaitPhase: number,
  gait: Gait,
  leg: LegName,
  config: CatLocomotionTuning = tuning.cat.locomotion,
): number {
  const offsets = phaseOffsetsFor(gait, config);
  const index = LEG_ORDER.indexOf(leg);
  const offset = index >= 0 ? (offsets[index] ?? 0) : 0;
  return wrap01(gaitPhase + offset);
}

/** Semi-implicit spring integration, shared by the tail and both ears. */
export function integrateSpring(
  state: Readonly<SpringState>,
  target: number,
  stiffness: number,
  damping: number,
  dt: number,
): SpringState {
  const acceleration = (target - state.position) * stiffness - state.velocity * damping;
  const velocity = state.velocity + acceleration * dt;
  return { position: state.position + velocity * dt, velocity };
}

export class QuadrupedLocomotion {
  private gait: Gait = 'idle';
  private phase = 0;
  private readonly legs: Record<LegName, LegState> = {
    frontLeft: createLegState(),
    frontRight: createLegState(),
    hindLeft: createLegState(),
    hindRight: createLegState(),
  };
  private readonly tail: SpringState[];
  private ears: [SpringState, SpringState] = [createSpringState(), createSpringState()];
  private spinePitch = 0;
  private spineRoll = 0;
  private headYaw = 0;
  private headPitch = 0;
  private previousForwardSpeed = 0;
  private previousYaw = 0;
  private motionInitialized = false;

  constructor(
    private readonly skeleton: SkeletonDescription,
    private readonly physics: GroundProbe,
    private readonly config: CatLocomotionTuning = tuning.cat.locomotion,
  ) {
    this.tail = Array.from({ length: skeleton.tailJointCount }, createSpringState);
  }

  update(frame: LocomotionFrame): QuadrupedPose {
    const speed = Math.hypot(frame.velocity.x, frame.velocity.z);
    this.gait = gaitForSpeed(speed, this.config.gaitSpeedThresholds);
    this.phase = wrap01(this.phase + gaitFrequency(speed, this.gait, this.config) * frame.dt);

    const forward = forwardFromYaw(frame.yaw);
    const right = rightFromYaw(frame.yaw);
    const forwardSpeed = dot(frame.velocity, forward);
    const lateralSpeed = dot(frame.velocity, right);
    const acceleration =
      this.motionInitialized && frame.dt > 0
        ? (forwardSpeed - this.previousForwardSpeed) / frame.dt
        : 0;
    const turnRate =
      this.motionInitialized && frame.dt > 0
        ? angleDelta(this.previousYaw, frame.yaw) / frame.dt
        : 0;

    this.updateSpine(acceleration, turnRate, frame.dt);
    this.updateHead(frame, forward, speed);
    this.updateTail(lateralSpeed, turnRate, frame.dt);
    this.updateEars(forwardSpeed, turnRate, frame.dt);

    this.previousForwardSpeed = forwardSpeed;
    this.previousYaw = frame.yaw;
    this.motionInitialized = true;

    return {
      gait: this.gait,
      phase: this.phase,
      legs: {
        frontLeft: this.updateLeg('frontLeft', frame, forward, speed),
        frontRight: this.updateLeg('frontRight', frame, forward, speed),
        hindLeft: this.updateLeg('hindLeft', frame, forward, speed),
        hindRight: this.updateLeg('hindRight', frame, forward, speed),
      },
      spinePitch: this.spinePitch,
      spineRoll: this.spineRoll,
      headYaw: this.headYaw,
      headPitch: this.headPitch,
      tail: this.tail.map((joint) => joint.position),
      ears: [this.ears[0].position, this.ears[1].position],
    };
  }

  private updateLeg(leg: LegName, frame: LocomotionFrame, forward: Vec3, speed: number): LegPose {
    const state = this.legs[leg];
    const root = this.skeleton.legRootOffsets[leg];
    const rootWorld = localToWorld(root, frame.position, frame.yaw);
    const phase = legPhaseAt(this.phase, this.gait, leg, this.config);
    const stance = this.gait !== 'idle' && phase < this.config.stanceFraction;

    if (!frame.grounded) {
      const tuckedLocal = vec3(root.x, root.y - this.skeleton.legLength, root.z);
      state.footWorld = localToWorld(tuckedLocal, frame.position, frame.yaw);
      state.swingStart = cloneVec3(state.footWorld);
      state.inStance = false;
      state.initialized = true;
    } else if (this.gait === 'idle') {
      state.footWorld = this.projectToGround(rootWorld, frame.position.y);
      state.swingStart = cloneVec3(state.footWorld);
      state.inStance = true;
      state.initialized = true;
    } else {
      if (!state.initialized) {
        state.footWorld = this.projectToGround(rootWorld, frame.position.y);
        state.swingStart = cloneVec3(state.footWorld);
        state.inStance = stance;
        state.initialized = true;
      }

      if (stance) {
        if (!state.inStance) {
          state.footWorld = this.projectToGround(state.footWorld, frame.position.y);
        }
        state.inStance = true;
      } else {
        if (state.inStance) state.swingStart = cloneVec3(state.footWorld);
        state.inStance = false;

        const stride = strideLengthForSpeed(speed, this.gait, this.config);
        const frequency = gaitFrequency(speed, this.gait, this.config);
        const bodyTravelPerCycle = frequency > 0 ? speed / frequency : stride;
        const lead = (bodyTravelPerCycle * this.config.stanceFraction) / 2;
        const target = this.projectToGround(
          vec3(rootWorld.x + forward.x * lead, rootWorld.y, rootWorld.z + forward.z * lead),
          frame.position.y,
        );
        const swingPhase = (phase - this.config.stanceFraction) / (1 - this.config.stanceFraction);
        const blend = smoothstep(swingPhase);
        state.footWorld = vec3(
          mix(state.swingStart.x, target.x, blend),
          mix(state.swingStart.y, target.y, blend) +
            Math.sin(Math.PI * swingPhase) * this.config.strideHeight,
          mix(state.swingStart.z, target.z, blend),
        );
      }
    }

    const localFoot = worldToLocal(state.footWorld, frame.position, frame.yaw);
    const solved = solveTwoBoneLeg(root, localFoot, this.skeleton.legLength);
    return {
      phase,
      stance,
      root: cloneVec3(root),
      knee: solved.knee,
      foot: solved.foot,
      footWorld: cloneVec3(state.footWorld),
    };
  }

  /**
   * Ground under `point`, always probed from a fixed height above the cat's base.
   *
   * The probe origin must never come from the point being projected. A stance paw
   * is re-projected from its own last position, so a query that ever returns its
   * own input - which is exactly what a probe starting inside a wall used to do -
   * ratchets that paw upward by one probe height every gait cycle.
   */
  private projectToGround(point: Vec3, baseY: number): Vec3 {
    const ground = this.physics.groundHeightAt(
      point.x,
      point.z,
      baseY + this.config.groundProbeHeight,
    );
    return vec3(point.x, ground ?? baseY, point.z);
  }

  private updateSpine(acceleration: number, turnRate: number, dt: number): void {
    const pitchAmount = clamp(acceleration / this.config.accelerationForFullLean, -1, 1);
    const rollAmount = clamp(turnRate / this.config.turnRateForFullLean, -1, 1);
    this.spinePitch = damp(
      this.spinePitch,
      -pitchAmount * this.config.spineLeanPitch,
      this.config.spineLeanDamping,
      dt,
    );
    this.spineRoll = damp(
      this.spineRoll,
      -rollAmount * this.config.spineLeanRoll,
      this.config.spineLeanDamping,
      dt,
    );
  }

  private updateHead(frame: LocomotionFrame, forward: Vec3, speed: number): void {
    const target =
      frame.headLookTarget ??
      (speed >= this.config.gaitSpeedThresholds.walk
        ? vec3(
            frame.position.x + frame.velocity.x,
            frame.position.y + frame.velocity.y,
            frame.position.z + frame.velocity.z,
          )
        : vec3(
            frame.position.x + forward.x,
            frame.position.y + forward.y,
            frame.position.z + forward.z,
          ));
    const direction = sub(target, frame.position);
    const horizontal = Math.hypot(direction.x, direction.z);
    const targetYaw = Math.atan2(-direction.x, -direction.z);
    const localYaw = clamp(
      angleDelta(frame.yaw, targetYaw),
      -this.config.headLookMaxYaw,
      this.config.headLookMaxYaw,
    );
    const localPitch = clamp(
      Math.atan2(direction.y, horizontal),
      -this.config.headLookMaxPitch,
      this.config.headLookMaxPitch,
    );
    this.headYaw = damp(this.headYaw, localYaw, this.config.headLookDamping, frame.dt);
    this.headPitch = damp(this.headPitch, localPitch, this.config.headLookDamping, frame.dt);
  }

  private updateTail(lateralSpeed: number, turnRate: number, dt: number): void {
    const totalTarget = clamp(
      -(lateralSpeed + turnRate * this.skeleton.bodyLength) * this.config.tailVelocityInfluence,
      -this.config.tailMaxBend,
      this.config.tailMaxBend,
    );
    const perJointTarget = this.tail.length > 0 ? totalTarget / this.tail.length : totalTarget;

    for (let index = 0; index < this.tail.length; index++) {
      const target = index === 0 ? perJointTarget : (this.tail[index - 1]?.position ?? 0);
      const current = this.tail[index];
      if (!current) continue;
      this.tail[index] = integrateSpring(
        current,
        target,
        this.config.tailStiffness,
        this.config.tailDamping,
        dt,
      );
    }
  }

  private updateEars(forwardSpeed: number, turnRate: number, dt: number): void {
    const speedTilt = -forwardSpeed * this.config.earVelocityInfluence;
    const turnTilt = turnRate * this.config.earTurnInfluence;
    const targets: [number, number] = [
      clamp(speedTilt - turnTilt, -this.config.earMaxTilt, this.config.earMaxTilt),
      clamp(speedTilt + turnTilt, -this.config.earMaxTilt, this.config.earMaxTilt),
    ];
    this.ears = [
      integrateSpring(
        this.ears[0],
        targets[0],
        this.config.earStiffness,
        this.config.earDamping,
        dt,
      ),
      integrateSpring(
        this.ears[1],
        targets[1],
        this.config.earStiffness,
        this.config.earDamping,
        dt,
      ),
    ];
  }
}

function phaseOffsetsFor(gait: Gait, config: CatLocomotionTuning): QuadrupedPhaseOffsets {
  return gait === 'idle' ? IDLE_PHASE_OFFSETS : config.legPhaseOffsets[gait];
}

function solveTwoBoneLeg(
  root: Vec3,
  requestedFoot: Vec3,
  totalLength: number,
): { knee: Vec3; foot: Vec3 } {
  const delta = sub(requestedFoot, root);
  const requestedDistance = lengthOf(delta);
  const direction = requestedDistance > Number.EPSILON ? normalize(delta) : vec3(0, -1, 0);
  const reachableDistance = Math.min(requestedDistance, totalLength);
  const foot = vec3(
    root.x + direction.x * reachableDistance,
    root.y + direction.y * reachableDistance,
    root.z + direction.z * reachableDistance,
  );
  const halfDistance = reachableDistance / 2;
  const segmentLength = totalLength / 2;
  const bendLength = Math.sqrt(
    Math.max(0, segmentLength * segmentLength - halfDistance * halfDistance),
  );
  const bendSign = root.z < 0 ? 1 : -1;
  const preferredBend = vec3(0, 0, bendSign);
  const projection = dot(preferredBend, direction);
  let bend = vec3(
    preferredBend.x - direction.x * projection,
    preferredBend.y - direction.y * projection,
    preferredBend.z - direction.z * projection,
  );
  if (lengthOf(bend) <= Number.EPSILON) bend = vec3(1, 0, 0);
  bend = normalize(bend);

  return {
    knee: vec3(
      root.x + direction.x * halfDistance + bend.x * bendLength,
      root.y + direction.y * halfDistance + bend.y * bendLength,
      root.z + direction.z * halfDistance + bend.z * bendLength,
    ),
    foot,
  };
}

function createLegState(): LegState {
  return {
    initialized: false,
    inStance: true,
    footWorld: vec3(),
    swingStart: vec3(),
  };
}

function createSpringState(): SpringState {
  return { position: 0, velocity: 0 };
}

function forwardFromYaw(yaw: number): Vec3 {
  return vec3(-Math.sin(yaw), 0, -Math.cos(yaw));
}

function rightFromYaw(yaw: number): Vec3 {
  return vec3(Math.cos(yaw), 0, -Math.sin(yaw));
}

function localToWorld(local: Vec3, position: Vec3, yaw: number): Vec3 {
  const cosine = Math.cos(yaw);
  const sine = Math.sin(yaw);
  return vec3(
    position.x + cosine * local.x + sine * local.z,
    position.y + local.y,
    position.z - sine * local.x + cosine * local.z,
  );
}

function worldToLocal(world: Vec3, position: Vec3, yaw: number): Vec3 {
  const cosine = Math.cos(yaw);
  const sine = Math.sin(yaw);
  const x = world.x - position.x;
  const z = world.z - position.z;
  return vec3(cosine * x - sine * z, world.y - position.y, sine * x + cosine * z);
}

function tupleToVec3(tuple: [number, number, number]): Vec3 {
  return vec3(tuple[0], tuple[1], tuple[2]);
}

function cloneVec3(value: Vec3): Vec3 {
  return vec3(value.x, value.y, value.z);
}

function mix(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

function smoothstep(value: number): number {
  const clamped = clamp(value, 0, 1);
  return clamped * clamped * (3 - 2 * clamped);
}

function wrap01(value: number): number {
  return ((value % 1) + 1) % 1;
}
